<template>
    <font-awesome-icon v-if="isLoading" icon="spinner" spin/>
    <div v-else>
        <page-title title="Rulesets" icon="pe-7s-note2"/>

        <b-row>
            <b-col cols="12" lg="6">
                <b-card>
                    <b-card-title>RULESETS</b-card-title>

                    <b-table :fields="supplyChainsTableFields" :items="supplyChains">
                        <template #cell(select)="data">
                            <b-button v-if="selectedSupplyChainIdentifier !== data.item.identifier" variant="outline-primary" size="sm" @click="selectSupplyChain(data.item.identifier)">
                                Selecteer
                            </b-button>
                            <b-button v-else disabled variant="primary" size="sm">Geselecteerd</b-button>
                        </template>
                    </b-table>
                </b-card>

                <b-card class="my-4">
                    <b-card-title>ZOEK PRODUCT</b-card-title>
                    <p><small><i>Vind de ruleset waar het product momenteel in zit</i></small></p>

                    <form @submit.prevent="searchProduct" class="d-block mb-4">
                        <b-input type="text" v-model.trim="productToSearch" :disabled="isSearchingProduct" class="d-inline-block w-50"/>
                        <b-button type="submit" variant="primary" :disabled="!productToSearch.length || isSearchingProduct" class="ml-2">Zoeken</b-button>
                    </form>

                    <span v-if="typeof searchedProductSupplyChain === 'string'">
                            Dit product zit in ruleset: <span class="text-primary">{{ searchedProductSupplyChain }}</span>
                        </span>
                    <span v-else-if="searchedProductSupplyChain === false" class="text-danger">
                            Product niet gevonden
                        </span>
                </b-card>
            </b-col>
            <b-col cols="12" lg="6">
                <b-collapse :visible="!!selectedSupplyChainIdentifier">
                    <b-card>
                        <b-card-title>VOEG PRODUCTEN TOE AAN GESELECTEERDE RULESET</b-card-title>
                        <p><small><i>Komma-gescheiden</i></small></p>

                        <form @submit.prevent="addProducts" class="text-right">
                            <b-textarea v-model.trim="productsToAdd" type="text" size="sm" :disabled="isAddingProducts"/>
                            <b-button type="submit" variant="success" :disabled="!productsToAdd.length || isAddingProducts" class="mt-2">Toevoegen</b-button>
                        </form>
                    </b-card>

                    <b-card class="mt-4">
                        <b-card-title>PRODUCTEN IN RULESET</b-card-title>
                        <p><small><i>Eerste {{ numProductsInSupplyChainToShow }} producten</i></small></p>

                        <b-row v-if="!isLoadingProducts">
                            <b-col>
                                <b-table small :fields="productsInSupplyChainFields" :items="productsInSupplyChain.slice(0, numProductsInSupplyChainToShow / 2)"/>
                            </b-col>
                            <b-col>
                                <b-table small :fields="productsInSupplyChainFields" :items="productsInSupplyChain.slice(numProductsInSupplyChainToShow / 2)"/>
                            </b-col>
                        </b-row>
                        <font-awesome-icon v-else icon="spinner" spin/>
                    </b-card>
                </b-collapse>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import axios from "axios";
import PageTitle from "@/components/PageTitle.vue";

export default {
    name: "SleSupplyChainIndex",
    components: {PageTitle},

    data() {
        return {
            isLoading: true,
            isAddingProducts: false,
            isSearchingProduct: false,
            isLoadingProducts: false,
            supplyChains: [],
            selectedSupplyChainIdentifier: '',
            productsToAdd: '',
            productToSearch: '',
            productsInSupplyChain: [],
            searchedProductSupplyChain: null,
            numProductsInSupplyChainToShow: 20,
        }
    },

    mounted() {
        this.getSupplyChains()
    },

    computed: {
        supplyChainsTableFields() {
            return [
                {
                    key: "identifier",
                    label: "Identifier",
                },
                {
                    key: "select",
                    label: "Selecteer",
                },
            ]
        },
        productsInSupplyChainFields() {
            return [
                {
                    key: 'identifier',
                },
            ]
        },
    },

    methods: {
        getSupplyChains() {
            axios.get(`/api/proxy/sle/api/supplychains`)
                .then(response => {
                    this.supplyChains = response.data
                    this.isLoading = false
                })
                .catch(e => {
                    this.$alert(`Fout bij het ophalen van supply chains`, 'danger')
                })
        },
        selectSupplyChain(identifier) {
            this.selectedSupplyChainIdentifier = identifier;
        },
        async getProductsInSupplyChain() {
            this.isLoadingProducts = true
            return axios.get(`/api/proxy/sle/api/supplychains/${this.selectedSupplyChainIdentifier}`, {
                params: {
                    include: 'products',
                    limit: `products:${this.numProductsInSupplyChainToShow}`,
                }
            })
                .then(response => {
                    this.productsInSupplyChain = response.data.products ?? []
                })
                .catch(e => {
                    this.$alert(`Fout bij het ophalen van producten`, 'danger')
                })
                .finally(() => this.isLoadingProducts = false)
        } ,
        async addProducts() {
            this.isAddingProducts = true
            const explodedProducts = this.productsToAdd.split(',')
                .map(identifier => identifier.trim())

            for (let i in explodedProducts) {
                let productIdentifier = explodedProducts[i]
                await axios.patch(`/api/proxy/sle/api/products/${productIdentifier}`, {
                    ruleset_identifier: this.selectedSupplyChainIdentifier,
                })
                    .then(() => {
                        delete explodedProducts[i]
                    })
                    .catch(e => {
                        console.error(e);
                        this.$alert(`Product '${productIdentifier}' mislukt: ${e.message}`, 'danger')
                    })
            }

            this.productsToAdd = explodedProducts
                .filter(identifier => identifier)
                .join(',')

            if (!this.productsToAdd) {
                this.$alert('De producten zijn toegevoegd')
            }
            else {
                this.$alert('Niet alle producten zijn toegevoegd. De producten die niet zijn toegevoegd zijn blijven staan in het invoerveld.', 'warning', true)
            }

            this.isAddingProducts = false
        },
        searchProduct() {
            this.isSearchingProduct = true
            this.searchedProductSupplyChain = null

            axios.get(`/api/proxy/sle/api/products/${this.productToSearch}`, {
                params: {
                    include: 'ruleset',
                },
            })
                .then(response => {
                    this.searchedProductSupplyChain = response.data.ruleset?.identifier ?? false
                })
                .catch(e => {
                    console.error(e);
                    this.searchedProductSupplyChain = false
                })
                .finally(() => this.isSearchingProduct = false)
        },
    },

    watch: {
        selectedSupplyChainIdentifier(val) {
            this.getProductsInSupplyChain()
        },
        productToSearch() {
            this.searchedProductSupplyChain = null
        },
    },
}
</script>
